import { useReducer } from 'react';
import { DispatchType } from '../constants';
import type {
    ChatMessage,
    ChatState,
    ConversationContextData,
    PlanProgressPayload,
    StudioAIConversation,
    StudioAIState,
} from '../types';

export const initialChatState: StudioAIState = {
    messages: [],
    conversations: [],
    chatState: 'idle',
    error: null,
    isLoading: false,
    currentStatus: null,
    currentThreadId: null,
    currentParentMessageId: '0',
    planProgress: null,
    conversationContextData: null,
};

export type StudioAIAction =
    | { type: DispatchType.AddMessage; payload: ChatMessage }
    | {
          type: DispatchType.UpdateMessage;
          payload: { messageId: string; updates: Partial<ChatMessage> };
      }
    | {
          type: DispatchType.SetMessages;
          payload: ChatMessage[] | ((prevMessages: ChatMessage[]) => ChatMessage[]);
      }
    | { type: DispatchType.SetConversations; payload: StudioAIConversation[] }
    | { type: DispatchType.AddConversation; payload: StudioAIConversation }
    | {
          type: DispatchType.UpdateConversation;
          payload: { conversationId: string; updates: Partial<StudioAIConversation> };
      }
    | { type: DispatchType.RemoveConversation; payload: string }
    | { type: DispatchType.SetChatState; payload: ChatState }
    | { type: DispatchType.SetError; payload: string | null }
    | { type: DispatchType.SetLoading; payload: boolean }
    | { type: DispatchType.SetCurrentStatus; payload: string | null }
    | { type: DispatchType.SetThreadId; payload: string | null }
    | { type: DispatchType.SetParentMessageId; payload: string }
    | { type: DispatchType.SetPlanProgress; payload: PlanProgressPayload | null }
    | { type: DispatchType.SetConversationContextData; payload: ConversationContextData | null }
    | { type: DispatchType.ClearError }
    | { type: DispatchType.Reset };

function studioAIReducer(state: StudioAIState, action: StudioAIAction): StudioAIState {
    switch (action.type) {
        case DispatchType.AddMessage:
            if (state.messages.some((m) => m.id === action.payload.id)) return state;
            return { ...state, messages: [...state.messages, action.payload] };

        case DispatchType.UpdateMessage:
            return {
                ...state,
                messages: state.messages.map((msg) =>
                    msg.id === action.payload.messageId
                        ? { ...msg, ...action.payload.updates }
                        : msg,
                ),
            };

        case DispatchType.SetMessages:
            return {
                ...state,
                messages:
                    typeof action.payload === 'function'
                        ? action.payload(state.messages)
                        : action.payload,
            };

        case DispatchType.SetConversations:
            return { ...state, conversations: action.payload };

        case DispatchType.AddConversation:
            return { ...state, conversations: [...state.conversations, action.payload] };

        case DispatchType.UpdateConversation:
            return {
                ...state,
                conversations: state.conversations.map((conversation) =>
                    conversation.conversationId === action.payload.conversationId
                        ? { ...conversation, ...action.payload.updates }
                        : conversation,
                ),
            };

        case DispatchType.RemoveConversation:
            return {
                ...state,
                conversations: state.conversations.filter(
                    (conversation) => conversation.conversationId !== action.payload,
                ),
            };

        case DispatchType.SetChatState:
            return { ...state, chatState: action.payload };

        case DispatchType.SetError:
            return { ...state, error: action.payload };

        case DispatchType.SetLoading:
            return { ...state, isLoading: action.payload };

        case DispatchType.SetCurrentStatus:
            return { ...state, currentStatus: action.payload };

        case DispatchType.SetThreadId:
            return { ...state, currentThreadId: action.payload };

        case DispatchType.SetParentMessageId:
            return { ...state, currentParentMessageId: action.payload };

        case DispatchType.SetPlanProgress:
            return { ...state, planProgress: action.payload };

        case DispatchType.SetConversationContextData:
            return { ...state, conversationContextData: action.payload };

        case DispatchType.ClearError:
            return { ...state, error: null };

        case DispatchType.Reset:
            return initialChatState;

        default:
            return state;
    }
}

export function useChatReducer() {
    return useReducer(studioAIReducer, initialChatState);
}
